import { z } from 'zod';

import type { AuthorizationTier } from '../../libs/auth/rbac';
import { MrrMonthlySnapshotSchema } from '../stripe';

export const RevenueKindSchema = z.enum([
  'unpaid',
  'no-credential',
  'provider-error',
  'stale',
  'loaded',
]);
export type RevenueKind = z.infer<typeof RevenueKindSchema>;

const RevenueUnpaidSchema = z.object({ kind: z.literal('unpaid') });
const RevenueNoCredentialSchema = z.object({
  kind: z.literal('no-credential'),
});
const RevenueProviderErrorSchema = z.object({
  kind: z.literal('provider-error'),
  months: z.array(MrrMonthlySnapshotSchema),
});
const RevenueStaleSchema = z.object({
  kind: z.literal('stale'),
  months: z.array(MrrMonthlySnapshotSchema),
  freshness: z.string().datetime(),
  skipReason: z.enum(['source-invalidated', 'owner-unauthorized']).optional(),
});
const RevenueLoadedSchema = z.object({
  kind: z.literal('loaded'),
  months: z.array(MrrMonthlySnapshotSchema),
  freshness: z.string().datetime(),
});

export const RevenueResponseSchema = z.discriminatedUnion('kind', [
  RevenueUnpaidSchema,
  RevenueNoCredentialSchema,
  RevenueProviderErrorSchema,
  RevenueStaleSchema,
  RevenueLoadedSchema,
]);
export type RevenueResponse = z.infer<typeof RevenueResponseSchema>;

// ---------------------------------------------------------------------------
// Bounded Summary tier (#2851 `revenue.read`)
//
// A real server-produced response shape, not a navigation treatment: the summary
// keeps counts and percentages and drops every currency amount, so a role at the
// summary tier can read the trend without reading the money. Each member is the
// SAME schema with only `months` narrowed, so a field added to the detail
// response cannot silently appear here.
// ---------------------------------------------------------------------------

/** Month, customer counts and retention percentages — no currency amounts. */
export const MrrMonthlySummarySchema = MrrMonthlySnapshotSchema.pick({
  month: true,
  customers: true,
  retention: true,
});
export type MrrMonthlySummary = z.infer<typeof MrrMonthlySummarySchema>;

const summaryMonths = z.array(MrrMonthlySummarySchema);

export const RevenueSummaryResponseSchema = z.discriminatedUnion('kind', [
  RevenueUnpaidSchema,
  RevenueNoCredentialSchema,
  RevenueProviderErrorSchema.extend({ months: summaryMonths }),
  RevenueStaleSchema.extend({ months: summaryMonths }),
  RevenueLoadedSchema.extend({ months: summaryMonths }),
]);
export type RevenueSummaryResponse = z.infer<
  typeof RevenueSummaryResponseSchema
>;

export type RevenueContractVariant = RevenueSummaryResponse | RevenueResponse;

/**
 * The tiers that carry an answer at all. `denied` is deliberately absent: a
 * refusal is not a narrower payload, and a projector that accepted it would hand
 * back a summary for a caller the policy refused.
 */
export type AllowedRevenueTier = Exclude<AuthorizationTier, 'denied'>;

/**
 * Project a revenue answer onto the tier the policy granted. Only `full-detail`
 * yields currency amounts; every other ALLOWED tier collapses to the bounded
 * summary, which is fail-closed by construction.
 *
 * The overloads make the tier prove the shape at the CALL SITE, so a consumer
 * that reads `waterfall.closingMrr` off the result cannot compile unless it has
 * already established the detail tier — and no caller can reach this function
 * with a denial, because `denied` is not in the accepted input type. The
 * implementation still throws on one, so a caller that casts past the types does
 * not get a summary either.
 */
export function selectRevenueContractVariant(
  response: unknown,
  tier: 'full-detail',
): RevenueResponse;
export function selectRevenueContractVariant(
  response: unknown,
  tier: Exclude<AllowedRevenueTier, 'full-detail'>,
): RevenueSummaryResponse;
export function selectRevenueContractVariant(
  response: unknown,
  tier: AllowedRevenueTier,
): RevenueContractVariant;
export function selectRevenueContractVariant(
  response: unknown,
  tier: AuthorizationTier,
): RevenueContractVariant {
  if (tier === 'denied') {
    throw new Error('revenue: a denied decision has no payload to project');
  }
  return tier === 'full-detail'
    ? RevenueResponseSchema.parse(response)
    : RevenueSummaryResponseSchema.parse(response);
}

export const AccountRouteErrorSchema = z.object({
  error: z.enum([
    'unauthenticated',
    'invalid_request',
    'org_not_found',
    'payment_required',
    'unavailable',
    'unknown_connection',
    'no_credential',
    'binding_conflict',
    'admin_required',
  ]),
});
export const WorkspaceIntegrationSourceResponseSchema = z.object({
  ok: z.literal(true),
  results: z.array(
    z.object({
      workspaceSlug: z.string().min(1),
      status: z.enum([
        'bound',
        'cleared',
        'conflict',
        'forbidden',
        'unavailable',
      ]),
    }),
  ),
});
export type WorkspaceIntegrationSourceResponse = z.infer<
  typeof WorkspaceIntegrationSourceResponseSchema
>;
export const IntegrationCredentialSaveResponseSchema = z.object({
  connectionId: z.string().min(1),
});
